import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { queries } from '@api';

import { EXECUTION_STEP_STATUSES, TERMINAL_REVIEW_STATES } from './api.aiReviews.constants';
import {
  retrieveAiReview,
  retrieveAiReviewsStats,
  retrieveLastReview,
  retrieveMyReviews,
  retrievePublicAiReview,
} from './api.aiReviews.requests';

import type { AIReviewFetchResponse, MyReviewsResponse } from './api.aiReviews.types';

const TERMINAL_STATES = TERMINAL_REVIEW_STATES;

const POLLING_ACTIVE = 1000;

/**
 * Backoff sequence for pending/idle states (ms):
 * 1 → 2 → 3 → 5 (cap)
 *
 * Keeps polling responsive for newly created analyses where backend
 * typically starts processing within 5-15 seconds. After 4 polls (~6s)
 * settles at 5s intervals — fast enough to detect state changes promptly
 * while staying reasonable on server load.
 */
const PENDING_BACKOFF_MS = [1000, 2000, 3000, 5000];

/**
Per-query backoff state. Each query gets its own instance to avoid cross-query interference.
*/
export type PollState = { count: number; lastState: string };

/**
Creates a fresh poll state instance. Exported for testing.
*/
export const createPollState = (): PollState => ({ count: 0, lastState: '' });

/**
 * Adaptive polling interval:
 * - No data yet: 1s (fast initial load)
 * - Processing (has RUNNING step): 1s
 * - Pending / Idle: backoff 1→2→3→5s (cap)
 * - Terminal: stop polling
 *
 * Backoff resets when state changes (e.g. pending → processing)
 *
 * @param query — React Query query object with current data state
 * @param pollState — per-query mutable state for backoff tracking
 */
export const getPollingInterval = (
  query: { state: { data?: AIReviewFetchResponse } },
  pollState: PollState,
): number | false => {
  const data = query.state.data;
  if (!data) return POLLING_ACTIVE;

  if (TERMINAL_STATES.has(data.state)) {
    pollState.count = 0;
    pollState.lastState = data.state;
    return false;
  }

  const hasRunning = data.steps?.some(
    (s) => s.status === EXECUTION_STEP_STATUSES.RUNNING,
  );

  if (hasRunning) {
    // Reset backoff when entering active processing
    pollState.count = 0;
    pollState.lastState = 'processing';
    return POLLING_ACTIVE;
  }

  // Pending / Idle — apply backoff
  const currentState = data.state;
  if (pollState.lastState !== currentState) {
    // State changed — reset backoff
    pollState.count = 0;
    pollState.lastState = currentState;
  }

  const index = Math.min(pollState.count, PENDING_BACKOFF_MS.length - 1);
  const interval = PENDING_BACKOFF_MS[index];
  pollState.count += 1;

  return interval;
};

/**
 * Per-review poll states — each review ID gets its own backoff tracker
 * so concurrent queries don't interfere with each other's backoff.
 */
const reviewPollStates = new Map<string, PollState>();

/**
 * Query options for retrieving single AI Review by ID
 */
export const getAiReviewOptions = (id: string) => {
  if (!reviewPollStates.has(id)) {
    reviewPollStates.set(id, createPollState());
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guaranteed by the .has() check above
  const pollState = reviewPollStates.get(id)!;

  return queryOptions({
    queryKey: queries.aiReviews.retrieve(id).queryKey,
    queryFn: () => retrieveAiReview(id),
    // Same adaptive polling as lastReview — stops on terminal states
    refetchInterval: (query) => getPollingInterval(query, pollState),
    // Keep previous data visible during refetch to prevent
    // flash of LoadingState between poll cycles
    placeholderData: keepPreviousData,
  });
};

/**
 * Query options for retrieving public/shared AI Review
 */
export const getPublicAiReviewOptions = (id: string) =>
  queryOptions({
    queryKey: queries.aiReviews.public(id).queryKey,
    queryFn: () => retrievePublicAiReview(id),
  });

/**
Polling interval for my-reviews list: 30s if any review is in-progress, otherwise stop
*/
const MY_REVIEWS_POLLING_MS = 30_000;

export const getMyReviewsPollingInterval = (query: {
  state: { data?: MyReviewsResponse };
}): number | false => {
  const reviews = query.state.data;
  if (!reviews?.length) return false;

  const hasInProgress = reviews.some(
    (r) => !TERMINAL_STATES.has(r.state),
  );
  return hasInProgress ? MY_REVIEWS_POLLING_MS : false;
};

/**
 * Query options for retrieving current user's reviews
 *
 * Polls every 30s while any review is in non-terminal state
 * (PENDING, PROCESSING) so UserMenu reflects status changes.
 * Stops polling when all reviews are terminal (COMPLETED/FAILED/CANCELLED).
 */
export const getMyReviewsOptions = queryOptions({
  queryKey: queries.aiReviews.myReviews.queryKey,
  queryFn: () => retrieveMyReviews(),
  refetchInterval: getMyReviewsPollingInterval,
});

/**
 * Query options for retrieving user's last review
 * Returns null/empty if user has no reviews
 *
 * Adaptive polling:
 * - 1s: no data yet / processing (has RUNNING steps)
 * - Backoff 1→2→4→8→12→24→30s: pending/idle (waiting for worker)
 * - Stop: terminal states (COMPLETED/FAILED/CANCELLED)
 */
const lastReviewPollState = createPollState();

export const getLastReviewOptions = queryOptions({
  queryKey: queries.aiReviews.lastReview.queryKey,
  queryFn: () => retrieveLastReview(),
  refetchInterval: (query) => getPollingInterval(query, lastReviewPollState),
  // Keep previous data visible during refetch to prevent
  // flash of NoDataState when backend briefly returns null
  placeholderData: keepPreviousData,
});

/**
 * Query options for retrieving AI Reviews statistics
 * Public endpoint, no authentication required
 */
export const getAiReviewsStatsOptions = queryOptions({
  queryKey: queries.aiReviews.stats.queryKey,
  queryFn: () => retrieveAiReviewsStats(),
});
